import CircularProgress from '@mui/material/CircularProgress'
import ModalWindow from 'components/molecules/ModalWindow'
import { CONSTS } from 'config/objectConst'
import { notificationUtils } from 'features/notifications'
import { useMemo } from 'react'
import { useSwitcher } from 'shared/hooks'
import { Button } from 'shared/ui'

import type * as API from '../../types'

import { api } from '../../api'
import css from './ConversationTogglers.module.scss'

const getEnabled = (key: keyof API.Actions, executions?: API.Execution[]) =>
	executions?.find(execution => execution.actions[key].enabled)

export function ConversationTogglers({
	id
}: { className?: string; id: string }) {
	const { data } = api.usePageQuery({
		$filter: `linkedin_contact/linkedin_entity_id eq '${id}'`,
		$orderby: 'created desc',
		$take: 1
	})

	const [resume, resumeOptions] = api.useResumeMutation()
	const [complete, completeOptions] = api.useCompleteMutation()

	const resumeSwitcher = useSwitcher()
	const cancelSwitcher = useSwitcher()

	const executionToResume = useMemo(
		() => getEnabled('resume', data?.documents),
		[data]
	)

	const executionToCancel = useMemo(
		() => getEnabled('complete', data?.documents),
		[data]
	)

	const isDisabledResume = executionToResume === undefined
	const isDisabledCancel = executionToCancel === undefined

	return (
		<menu className={css.root}>
			<li>
				<Button
					disabled={isDisabledResume}
					onClick={resumeSwitcher.on}
					variant='secondary'
				>
					{resumeOptions.isLoading ? (
						<CircularProgress
							classes={{ colorPrimary: 'material-loader-black' }}
							size={20}
						/>
					) : (
						'Resume conversation'
					)}
				</Button>

				{!isDisabledResume && (
					<ModalWindow
						closeHandler={resumeSwitcher.off}
						closeText='No'
						confirmHandler={() => {
							if (!resumeOptions.isLoading && executionToResume) {
								resume(executionToResume.id)
									.unwrap()
									.then(() => notificationUtils.snackById('n3.33'))
									.catch(console.error)
							}
						}}
						confirmText='Yes'
						loading={resumeOptions.isLoading}
						{...CONSTS.confirmationDialogs['c1.16']}
						open={resumeSwitcher.isOn}
					/>
				)}
			</li>

			<li>
				<Button
					disabled={isDisabledCancel}
					onClick={cancelSwitcher.on}
					variant='secondary'
				>
					{completeOptions.isLoading ? (
						<CircularProgress
							classes={{ colorPrimary: 'material-loader-black' }}
							size={20}
						/>
					) : (
						'Cancel conversation'
					)}
				</Button>

				{!isDisabledCancel && (
					<ModalWindow
						closeHandler={cancelSwitcher.off}
						closeText='No'
						confirmHandler={() => {
							if (!completeOptions.isLoading && executionToCancel) {
								complete(executionToCancel.id)
									.unwrap()
									.then(() => notificationUtils.snackById('n3.34'))
									.catch(console.error)
							}
						}}
						confirmText='Yes'
						loading={completeOptions.isLoading}
						{...CONSTS.confirmationDialogs['1.1']}
						open={cancelSwitcher.isOn}
					/>
				)}
			</li>
		</menu>
	)
}
